import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ErrorBoundary } from '@ohif/ui-next';
import Header, { getOidcUserFromSession } from '../../../ui-next/src/components/Header/Header';
import { Database, Search, ChevronRight } from 'lucide-react';

type DatastoreType = {
  id: string;
  name: string;
};

const API_BASE = (window as any).baseUrl || '';

const SkeletonTable: React.FC = () => (
  <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
    <div className="animate-pulse">
      <div className="grid grid-cols-12 gap-4 border-b border-gray-200 bg-gray-50 px-6 py-4">
        <div className="col-span-5 h-4 rounded bg-gray-200" />
        <div className="col-span-4 h-4 rounded bg-gray-200" />
        <div className="col-span-2 h-4 rounded bg-gray-200" />
        <div className="col-span-1 h-4 rounded bg-gray-200" />
      </div>

      {Array.from({ length: 5 }).map((_, index) => (
        <div
          key={index}
          className="grid grid-cols-12 items-center gap-4 border-b border-gray-100 px-6 py-5 last:border-b-0"
        >
          <div className="col-span-5 flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-gray-200" />
            <div className="h-4 w-40 rounded bg-gray-200" />
          </div>
          <div className="col-span-4 h-4 w-48 rounded bg-gray-200" />
          <div className="col-span-2 h-6 w-20 rounded-full bg-gray-200" />
          <div className="col-span-1 h-4 w-4 rounded bg-gray-200" />
        </div>
      ))}
    </div>
  </div>
);

const EmptyState: React.FC = () => (
  <div className="flex flex-col items-center justify-center rounded-lg border border-gray-200 bg-white px-6 py-16 text-center shadow-sm">
    <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-gray-50">
      <Database className="h-7 w-7 text-gray-400" />
    </div>

    <h3 className="text-lg font-semibold text-gray-900">No datastores available</h3>

    <p className="mt-2 max-w-md text-sm text-gray-500">
      No datastores are accessible with this account.
    </p>
  </div>
);

const DatastoreLanding: React.FC = () => {
  const navigate = useNavigate();

  const [datastores, setDatastores] = useState<DatastoreType[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  const loadDatastores = async () => {
    setLoading(true);
    setError(null);

    const user = getOidcUserFromSession();
    const userId = user?.uid;

    if (!userId) {
      setDatastores([]);
      setError('User not logged in.');
      setLoading(false);
      return;
    }

    localStorage.setItem('userId', userId);

    const token = user?.access_token || user?.id_token;

    if (!token) {
      setDatastores([]);
      setError('Missing authentication token. Please login again.');
      setLoading(false);
      return;
    }

    try {
      const url = `${API_BASE}/datastore_ids?user_id=${encodeURIComponent(userId)}`;

      const res = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      if (res.status === 401) {
        localStorage.removeItem('selectedDatastore');

        throw new Error('Unauthorized — missing or invalid Authorization header (401).');
      }

      if (!res.ok) {
        const txt = await res.text();
        throw new Error(`Datastore fetch failed: ${res.status} ${txt}`);
      }

      const raw = await res.json();

      const parsed: DatastoreType[] = (Array.isArray(raw) ? raw : []).map((item: any) => ({
        id: String(item.datastore_id),
        name: item.datastore_name ?? String(item.datastore_id),
      }));

      setDatastores(parsed);
    } catch (err: any) {
      setError(err?.message ?? 'Failed to load datastores');
      setDatastores([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDatastores();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSelect = (ds: DatastoreType) => {
    setSelectedId(ds.id);

    localStorage.setItem('selectedDatastore', JSON.stringify(ds.id));

    navigate('/');
  };

  const handleKeySelect = (e: React.KeyboardEvent, ds: DatastoreType) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleSelect(ds);
    }
  };

  const truncateId = (id: string, start = 12, end = 8) => {
    if (id.length <= start + end + 3) {
      return id;
    }

    return `${id.slice(0, start)}...${id.slice(-end)}`;
  };

  const filtered = useMemo(() => {
    if (!datastores) {
      return [];
    }

    if (!search.trim()) {
      return datastores;
    }

    const q = search.toLowerCase();

    return datastores.filter(
      d => d.name.toLowerCase().includes(q) || d.id.toLowerCase().includes(q)
    );
  }, [datastores, search]);

  return (
    <ErrorBoundary>
      <div className="flex min-h-screen flex-col bg-gray-50">
        {/* Lilly Header */}
        <Header
          menuOptions={[]}
          isSticky
          isReturnEnabled={false}
        />

        <main className="flex-1 px-6 py-8 md:px-10 lg:px-12">
          <div className="mx-auto max-w-7xl">
            {/* Page Header */}
            <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div>
                <h1 className="text-2xl font-semibold tracking-tight text-gray-900">
                  Central Imaging Service
                </h1>

                <p className="mt-1 text-sm text-gray-500">Select a datastore to continue</p>
              </div>

              {/* Search */}
              {!loading && datastores && datastores.length > 0 && (
                <div className="relative w-full md:w-80">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />

                  <input
                    aria-label="Search datastores"
                    placeholder="Search datastore..."
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    className="h-10 w-full rounded-md border border-gray-300 bg-white pl-9 pr-4 text-sm text-gray-900 outline-none transition focus:border-red-500 focus:ring-1 focus:ring-red-500"
                  />
                </div>
              )}
            </div>

            {/* Loading */}
            {loading && <SkeletonTable />}

            {/* Error */}
            {error && !loading && (
              <div className="mb-5 rounded-md border border-red-200 bg-red-50 px-5 py-4">
                <p className="text-sm font-medium text-red-800">{error}</p>
              </div>
            )}

            {/* Empty */}
            {!loading && (!datastores || datastores.length === 0) && <EmptyState />}

            {/* Table */}
            {!loading && datastores && datastores.length > 0 && (
              <>
                <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="border-b border-gray-200 bg-gray-50">
                        <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wide text-gray-600">
                          Datastore
                        </th>

                        <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wide text-gray-600">
                          Datastore ID
                        </th>

                        <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wide text-gray-600">
                          Status
                        </th>

                        <th className="w-12 px-4 py-4" />
                      </tr>
                    </thead>

                    <tbody>
                      {filtered.map(ds => {
                        const isSelected = selectedId === ds.id;

                        return (
                          <tr
                            key={ds.id}
                            role="button"
                            tabIndex={0}
                            onClick={() => handleSelect(ds)}
                            onKeyDown={e => handleKeySelect(e, ds)}
                            className={`group cursor-pointer border-b border-gray-100 transition-colors last:border-b-0 hover:bg-red-50 ${
                              isSelected ? 'bg-red-50' : 'bg-white'
                            }`}
                          >
                            {/* Datastore Name */}
                            <td className="px-6 py-4">
                              <div className="flex items-center gap-3">
                                <div
                                  className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg transition-colors ${
                                    isSelected ? 'bg-red-100' : 'bg-gray-100 group-hover:bg-red-100'
                                  }`}
                                >
                                  <Database
                                    className={`h-5 w-5 ${
                                      isSelected
                                        ? 'text-red-600'
                                        : 'text-gray-500 group-hover:text-red-600'
                                    }`}
                                  />
                                </div>

                                <div className="min-w-0">
                                  <div className="truncate text-sm font-semibold text-gray-900">
                                    {ds.name}
                                  </div>

                                  <div className="mt-0.5 text-xs text-gray-500">Datastore</div>
                                </div>
                              </div>
                            </td>

                            {/* ID */}
                            <td className="px-6 py-4">
                              <span
                                className="font-mono text-sm text-gray-600"
                                title={ds.id}
                              >
                                {truncateId(ds.id)}
                              </span>
                            </td>

                            {/* Status */}
                            <td className="px-6 py-4">
                              <span className="inline-flex items-center gap-1.5 rounded-full bg-green-50 px-3 py-1 text-xs font-medium text-green-700">
                                <span className="h-1.5 w-1.5 rounded-full bg-green-500" />
                                Accessible
                              </span>
                            </td>

                            {/* Arrow */}
                            <td className="px-4 py-4 text-right">
                              <ChevronRight
                                className={`h-5 w-5 transition-transform ${
                                  isSelected
                                    ? 'translate-x-1 text-red-600'
                                    : 'text-gray-400 group-hover:translate-x-1 group-hover:text-red-600'
                                }`}
                              />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* No Search Results */}
                {filtered.length === 0 && (
                  <div className="mt-6 rounded-lg border border-gray-200 bg-white px-6 py-10 text-center">
                    <Search className="mx-auto mb-3 h-6 w-6 text-gray-400" />

                    <p className="text-sm font-medium text-gray-700">No datastores found</p>

                    <p className="mt-1 text-xs text-gray-500">No datastores match "{search}".</p>
                  </div>
                )}

                {/* Count */}
                {filtered.length > 0 && (
                  <div className="mt-3 text-xs text-gray-500">
                    Showing {filtered.length} of {datastores.length} datastore
                    {datastores.length !== 1 ? 's' : ''}
                  </div>
                )}
              </>
            )}
          </div>
        </main>
      </div>
    </ErrorBoundary>
  );
};

export default DatastoreLanding;
